
import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';

interface DashboardMetrics {
  activeCompanies: number;
  advancesByStatus: Record<string, number>;
  successByMechanism: Record<string, { count: number; shareOfCollected: number }>;
  failureCounts: { sendFailed: number; abandoned: number };
  dateRange: { from: string | null; to: string | null };
}

const STATUS_LABELS: Record<string, string> = {
  requested: 'Pendientes',
  approved: 'Aprobados',
  disbursed: 'Desembolsados',
  in_collection: 'En cobro',
  collected: 'Cobrados',
  abandoned: 'Abandonados',
  cancelled: 'Cancelados',
};

const MECHANISM_LABELS: Record<string, string> = {
  PAC: 'Cobro bancario (PAC)',
};

const formatPercent = (n: number) => `${(n * 100).toFixed(1)}%`;

export default function AdminDashboardPage() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const base = getAdminEndpointUrl('metrics', 'dashboard');
      const url = qs.toString() ? `${base}?${qs.toString()}` : base;
      const data = await adminHttpClient.get<DashboardMetrics>(url);
      setMetrics(data);
    } catch {
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <AdminLayout title="Dashboard" subtitle="Métricas agregadas del sistema">
      <div className="space-y-6 max-w-6xl">
        <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-4">
          <label className="text-sm text-muted-foreground">Desde</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
          <label className="text-sm text-muted-foreground">Hasta</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
          <button type="button" onClick={() => { setFrom(''); setTo(''); }} className="text-xs text-muted-foreground underline">Limpiar</button>
        </div>

        {loading && <p className="text-sm text-muted-foreground">Cargando métricas…</p>}

        {!loading && metrics && (
          <>
            <section className="grid gap-4 md:grid-cols-4">
              <MetricCard label="Comercios activos" value={metrics.activeCompanies.toLocaleString('es-CL')} />
              <MetricCard
                label="Adelantos en vuelo"
                value={(
                  (metrics.advancesByStatus.approved ?? 0) +
                  (metrics.advancesByStatus.disbursed ?? 0) +
                  (metrics.advancesByStatus.in_collection ?? 0)
                ).toLocaleString('es-CL')}
                tone="info"
              />
              <MetricCard label="Payment requests fallidos" value={metrics.failureCounts.sendFailed.toLocaleString('es-CL')} tone="warn" />
              <MetricCard label="Cuotas abandonadas" value={metrics.failureCounts.abandoned.toLocaleString('es-CL')} tone="warn" />
            </section>

            <section className="rounded-xl border bg-card p-6">
              <h3 className="text-base font-semibold">Adelantos por estado</h3>
              <div className="mt-4 grid gap-2 sm:grid-cols-2 md:grid-cols-4">
                {Object.entries(metrics.advancesByStatus).map(([status, count]) => (
                  <div key={status} className="rounded-lg border border-border bg-background px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{STATUS_LABELS[status] ?? status}</p>
                    <p className="mt-1 text-xl font-semibold">{count.toLocaleString('es-CL')}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-xl border bg-card p-6">
              <h3 className="text-base font-semibold">Cuotas cobradas — distribución por mecanismo</h3>
              <div className="mt-4 space-y-3">
                {Object.entries(metrics.successByMechanism).map(([mech, stat]) => (
                  <div key={mech}>
                    <div className="flex items-center justify-between text-sm">
                      <span>{MECHANISM_LABELS[mech] ?? mech}</span>
                      <span className="text-muted-foreground">{stat.count.toLocaleString('es-CL')} · {formatPercent(stat.shareOfCollected)}</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div className="h-full bg-flux-primary" style={{ width: `${Math.min(100, stat.shareOfCollected * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function MetricCard({ label, value, tone = 'default' }: { label: string; value: string; tone?: 'default' | 'info' | 'warn' | 'error' }) {
  const toneClass = tone === 'info' ? 'text-sky-600' : tone === 'warn' ? 'text-amber-600' : tone === 'error' ? 'text-red-600' : 'text-foreground';
  return (
    <div className="rounded-xl border bg-card p-5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-2 text-2xl font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}
